import { notify, useModal } from '@billy/design-system'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { VatDeclarationsPeriod } from '@features/vatDeclarations/types/vatDeclarationsPeriod'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { FormComponent, useForm, useSegment } from '../../../../../hooks'
import { APIError, formatDate } from '../../../../../utils'
import { RegisterVatDeclarationPayload } from '../../../query-api'
import { getPeriodData } from '../../../utils/getPeriodData'
import { useRegisterVatDeclarationPayment } from '../hooks/useRegisterVatDeclarationPayment'
import { getDefaultValues, getValidationSchema, VatDeclarationsRegisterPeriodPaymentModalForm } from '../utils/formData'

type PaymentDate = Date | undefined | null

interface ContextState {
  closeModal: () => void
  Form: FormComponent
  isProcessing: boolean
  paymentDate: PaymentDate
  periodText: string
  periodTotalAmountDue: number
  submitForm: () => void
  handlePaymentDateChange: (date: Date | undefined | null) => void
}

const VatDeclarationsRegisterPeriodPaymentModalContext = createContext<ContextState | undefined>(undefined)

interface VatDeclarationsRegisterPeriodPaymentModalContextProps {
  children?: ReactNode
  modalId: string
  vatDeclaration: VatDeclarationsPeriod
}

export const VatDeclarationsRegisterPeriodPaymentModalContextProvider = ({
  children,
  modalId,
  vatDeclaration,
}: VatDeclarationsRegisterPeriodPaymentModalContextProps): ReactElement => {
  const { corrections, id, period, periodText, settledAmount } = vatDeclaration

  const [paymentDate, setPaymentDate] = useState<PaymentDate>(new Date())

  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { close: closeModal, isOpen: isModalOpen } = useModal(modalId)

  const periodData = getPeriodData(period || '')
  const currentCorrection = corrections?.find(({ salesTaxReturnId }) => salesTaxReturnId === id)

  const { registerPayment, isProcessing } = useRegisterVatDeclarationPayment({
    onError: (error: APIError | undefined) => {
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsRegisterPayment,
        message: error?.message || t('vat_declarations.register_payment_modal.fail'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsRegisterPayment,
        message: t('vat_declarations.register_payment_modal.success'),
        variant: 'success',
      })
    },
  })

  const { Form, handleSubmit, setValue, getValues } = useForm({
    defaultValues: useMemo(() => getDefaultValues(settledAmount || 0), [settledAmount]),
    validationSchema: useMemo(() => getValidationSchema(t), [t]),
  })

  const handleModalClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handlePaymentDateChange = useCallback((date: PaymentDate) => {
    setPaymentDate(date)
  }, [])

  const handleSubmitForm = useCallback(
    (values: VatDeclarationsRegisterPeriodPaymentModalForm) => {
      const registerPaymentPayload: RegisterVatDeclarationPayload = {
        accountId: values.paymentAccount,
        entryDate: formatDate(values.paymentDate),
        returnId: id,
      }

      registerPayment(registerPaymentPayload)

      if (periodData && currentCorrection) {
        track('VAT Declaration Payment Created (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          period_settled: periodData.periodSettled,
          year: periodData.year,
          context: 'direct',
          type: currentCorrection?.correctionNo > 0 ? 'correction' : 'normal',
        })
      }
    },
    [id, registerPayment, periodData, currentCorrection, track],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleSubmitForm)()
  }, [handleSubmit, handleSubmitForm])

  useEffect(() => {
    if (isModalOpen && !getValues('paymentAccount') && organization?.defaultBillBankAccountId) {
      setValue('paymentAccount', organization.defaultBillBankAccountId)
    }
  }, [getValues, isModalOpen, organization?.defaultBillBankAccountId, setValue])

  return (
    <VatDeclarationsRegisterPeriodPaymentModalContext.Provider
      value={{
        closeModal: handleModalClose,
        handlePaymentDateChange,
        Form,
        isProcessing,
        paymentDate,
        periodText,
        periodTotalAmountDue: settledAmount || 0,
        submitForm,
      }}
    >
      {children}
    </VatDeclarationsRegisterPeriodPaymentModalContext.Provider>
  )
}

export const useVatDeclarationsRegisterPeriodPaymentModal = () => {
  const context = useContext(VatDeclarationsRegisterPeriodPaymentModalContext)

  if (!context) {
    throw new Error('VatDeclarationsRegisterPeriodPaymentModalContext is missing in the module!')
  }

  return context
}
